import { useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';
import { Paper, Button, Typography, TextField, MenuItem, Stack, Chip, Link, Checkbox } from "@mui/material";
import TableView from '../components/TableView';
import Pagination from '../components/Pagination';
import SearchForm from '../components/SearchForm';
import useDataFetch from '../hooks/useDataFetch';
import useDataSelect from '../hooks/useDataSelect';
import { getFields, searchFields } from '../utils/fields';
import { useSnackbar } from '../contexts/SnackbarProvider';
import { formatCurrency, formatDate } from '../utils/format';
import RelatedMenu from '../components/RelatedMenu';
import GlobalError from '../components/GlobalError';
import { useSelectRow } from '../utils/useSelectRow';
const InvoicePage = ({ hasLimit }) => {
    const { showSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const { selectedItems, setSelectedItems, handleRowSelect, handleSelectAll } = useSelectRow('invoice_id');
    const location = useLocation();

    const initialStatus = location.state?.filterStatus
        ? (typeof location.state.filterStatus === 'string'
            ? location.state.filterStatus.split(',').map(Number) // '2,3' → [2,3]
            : [location.state.filterStatus])
        : []; // デフォルト

    const { dataset: statuses } = useDataSelect('/invoice/statuses');

    const {
        dataset,
        error,
        currentPage,
        totalPages,
        searchQuery,
        handleSearchChange,
        handleSearchSubmit,
        handleClearSearch,
        handlePageChange,
        refetch
    } = useDataFetch('/invoice', { invoice_status_id: initialStatus });

    const handleStatusChange = async ({
        id,
        status,
        payload = {},
        confirmMessage,
        successMessage,
        navigateTo,
        afterSuccess,
        force = false
    }) => {
        if (confirmMessage && !window.confirm(confirmMessage)) return;

        try {
            const res = await axiosInstance.post('/invoice/update-status', {
                Ids: [id],
                status,
                force,
                ...payload,
            });
            if (successMessage) {
                showSnackbar(res?.data?.message || '請求情報が更新されました', 'success');
            }
            if (afterSuccess) afterSuccess();
            if (navigateTo) navigate(navigateTo);

        } catch (err) {
            if (process.env.NODE_ENV === 'development') {
                console.error(err);
            }
            showSnackbar(err?.response?.data?.message || '更新に失敗しました', 'error');
        } finally {
            refetch();
        }
    };
    // 検索フィールド定義
    const fieldsToInclude = ['dates', 'client_name'];
    let searchItems = getFields(searchFields, fieldsToInclude);
    searchItems = [
        ...searchItems,
        {
            name: 'invoice_status_id',
            type: 'select',
            label: '請求状況',
            options: statuses.map((item) => ({
                value: item.invoice_status_id,
                label: item.status_name,
            }))
        },
    ];

    const handleRowClick = async (item) => {
        navigate(`/invoices/${item.invoice_id}`);
    };
    const handleBulkIssue = async () => {
        if (selectedItems.length === 0) {
            showSnackbar('請求書を選択してください', 'warning');
            return;
        }

        const confirmed = window.confirm(
            `${selectedItems.length}件の請求書を発行しますか？`
        );

        if (!confirmed) return;

        try {
            const res = await axiosInstance.post(
                '/invoice/issue-bulk',
                { invoice_ids: selectedItems },
                { responseType: 'blob' }
            );

            const blob = new Blob([res.data], { type: 'application/zip' });
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = url;
            a.download = 'invoices.zip';
            a.click();

            window.URL.revokeObjectURL(url);

            setSelectedItems([]);
            showSnackbar('ZIPをダウンロードしました', 'success');
            refetch();

        } catch (err) {
            console.error(err);

            showSnackbar(
                err?.response?.data?.message || '発行に失敗しました',
                'error'
            );
        }
    };
    /*
    const handleIssueSingle = async (invoiceId) => {
        const confirmed = window.confirm('この請求書を発行しますか？');
        if (!confirmed) return;

        try {
            const res = await axiosInstance.post(
                '/invoice/issued',
                { invoice_id: invoiceId },
                { responseType: 'blob' }
            );

            const blob = new Blob([res.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = url;
            a.download = `invoice_${invoiceId}.pdf`;
            a.click();

            window.URL.revokeObjectURL(url);

            showSnackbar('請求書をダウンロードしました', 'success');
            refetch();

        } catch (err) {
            console.error(err);

            showSnackbar(
                err?.response?.data?.message || '発行に失敗しました',
                'error'
            );
        }
    };
    */
    const handleIssueSingle = async (invoiceId) => {
        const confirmed = window.confirm('この請求書を発行しますか？');

        if (!confirmed) return;
        try {
            const res = await axiosInstance.post('/invoice/issued',
                { invoice_id: invoiceId },
                { responseType: 'blob' }
            );

            let filename = `invoice_${invoiceId}.pdf`;
            const disposition = res.headers['content-disposition'];
            if (disposition) {
                const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
                if (utf8Match?.[1]) {
                    filename = decodeURIComponent(utf8Match[1]);
                } else {
                    const normalMatch = disposition.match(/filename="?([^"]+)"?/i);
                    if (normalMatch?.[1]) { filename = normalMatch[1]; }
                }
            }

            const blob = new Blob(
                [res.data],
                { type: 'application/pdf' }
            );
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;

            document.body.appendChild(a);
            a.click();
            a.remove();

            window.URL.revokeObjectURL(url);
            showSnackbar('請求書をダウンロードしました', 'success');
            refetch();

        } catch (err) {
            console.error(err);
            showSnackbar(err?.response?.data?.message || '発行に失敗しました', 'error');
        }
    };
    const columns = [
        {
            label: (
                <Checkbox
                    checked={selectedItems.length === dataset.length}
                    indeterminate={selectedItems.length > 0 && selectedItems.length < dataset.length}
                    onChange={(e) => handleSelectAll(e, dataset)}
                />
            ),
            accessor: 'checkbox',
            render: (item) => (
                <Checkbox
                    checked={selectedItems.includes(item.invoice_id)}
                    onChange={() => handleRowSelect(item.invoice_id)}
                />
            ),
            width: '50px',
        },
        {
            label: '請求日',
            accessor: 'invoice_date',
            width: '100px',
            render: (item) => (formatDate(item.invoice_date))
        },
        {
            label: '請求書番号',
            accessor: 'invoice_number',
            width: '110px',
            render: (item) => (
                <Link
                    component="button"
                    onClick={() => handleRowClick(item)}>
                    {item.invoice_number}
                </Link>
            ),
        },
        {
            label: '取引先',
            accessor: 'client_name',
            width: '300px',
            render: (item) => (
                <Typography>
                    {item.client_name}
                </Typography>
            )
        },
        {
            label: '請求期間',
            accessor: 'period',
            width: '100px',
            render: (item) =>
                item.period_from && item.period_to ? (
                    <>
                        {formatDate(item.period_from)}
                        <br />
                        〜
                        <br />
                        {formatDate(item.period_to)}
                    </>
                ) : (
                    '-'
                )
        },
        {
            label: '金額',
            accessor: 'total_amount',
            width: '180px',
            render: (item) => (
                <>
                    {formatCurrency(item.total_amount)}
                    <br />
                    <Typography variant="caption">
                        税込 {formatCurrency(item.total_amount_including_tax)}
                    </Typography>
                </>
            )
        },
        {
            label: '発行',
            width: 100,
            render: (item) => (
                [1, 2].includes(Number(item.invoice_status_id)) && (
                    <Button
                        variant={Number(item.invoice_status_id) === 1 ? "contained" : "outlined"}
                        size="small"
                        fullWidth
                        onClick={() => handleIssueSingle(item.invoice_id)}
                    >
                        {Number(item.invoice_status_id) === 1 ? '発行' : '再発行'}
                    </Button>
                )
            )
        },
        {
            label: '請求状況',
            accessor: 'status_name',
            width: '130px',
            render: (item) => (
                <TextField
                    select
                    size="small"
                    value={item.invoice_status_id}
                    onChange={(e) => {
                        const newStatus = Number(e.target.value);
                        const statusName = statuses.find(
                            s => Number(s.invoice_status_id) === newStatus
                        )?.status_name;

                        if (
                            [1, 3, 4].includes(newStatus) &&
                            !window.confirm(`請求ステータスを「${statusName}」に変更しますか？`)
                        ) {
                            return;
                        }

                        handleStatusChange({
                            id: item.invoice_id,
                            status: e.target.value,
                            successMessage: '完了ステータスへ変更しました',
                        })
                    }}
                >
                    {statuses.map((status) => (
                        <MenuItem
                            key={status.invoice_status_id}
                            value={status.invoice_status_id}
                        >
                            {status.status_name}
                        </MenuItem>
                    ))}
                </TextField>
            )
        },
        {
            label: '注文番号',
            accessor: 'orders',
            width: '320px',
            render: (item) => (
                <Stack
                    direction="row"
                    gap={1}
                    flexWrap="wrap"
                >
                    {
                        (item.orders || [])
                            .filter(order => order.order_id)
                            .map((order) => (
                                <Chip
                                    key={order.order_id}
                                    label={order.order_number}
                                    clickable
                                    variant="outlined"
                                    color="primary"
                                    size="small"
                                    onClick={() => navigate(
                                        `/orders/${order.order_id}`
                                    )}
                                />
                            ))
                    }
                </Stack>
            )
        },
    ];
    const links = [
        { path: '/invoices/create', label: '請求作成', icon: 'request_quote' },
    ];
    if (error) return (<GlobalError errorMessage={error} />);

    return (
        <>
            <Typography variant="h1">請求一覧</Typography>
            <div className='grid2-2_1'>
                <SearchForm
                    searchQuery={searchQuery}
                    searchFields={searchItems}
                    handleSearchChange={handleSearchChange}
                    handleSearchSubmit={handleSearchSubmit}
                    handleClearSearch={handleClearSearch}
                />

                <RelatedMenu links={links}>
                </RelatedMenu>

            </div>

            <Paper variant='section'>
                <Button
                    variant="contained"
                    onClick={handleBulkIssue}
                    disabled={selectedItems.length === 0}
                >
                    選択分を一括発行
                </Button>
                <TableView
                    dataset={dataset}
                    columns={columns}
                    keyColumn="invoice_id"
                />

                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                />

            </Paper>
        </>
    );

};

export default InvoicePage;